import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const TechnicianAssignment = ({ technicians, workOrders, equipment, onRefresh }) => {
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [filterSkill, setFilterSkill] = useState('all');

  const allSkills = ['all', 'HVAC', 'Electrical', 'Plumbing', 'Equipment', 'General'];

  const filteredTechnicians = filterSkill === 'all'
    ? technicians
    : technicians?.filter(tech => tech?.skills?.includes(filterSkill));

  const getAvailabilityBadge = (availability) => {
    const config = {
      available: { label: 'Available', color: 'text-success', bg: 'bg-success/10', icon: 'CheckCircle2' },
      busy: { label: 'Busy', color: 'text-warning', bg: 'bg-warning/10', icon: 'Clock' },
      unavailable: { label: 'Unavailable', color: 'text-error', bg: 'bg-error/10', icon: 'XCircle' }
    };
    const cfg = config?.[availability] || config?.available;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${cfg?.bg} ${cfg?.color}`}>
        <Icon name={cfg?.icon} size={12} />
        {cfg?.label}
      </span>
    );
  };

  const getWorkloadColor = (workload) => {
    if (workload >= 5) return 'text-error';
    if (workload >= 3) return 'text-warning';
    return 'text-success';
  };

  const getTechnicianWorkOrders = (technicianId) => {
    return workOrders?.filter(wo => wo?.assigned_to === technicianId && wo?.status !== 'completed');
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-card rounded-xl border border-border p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <label className="text-sm font-medium text-foreground mb-2 block">Filter by Skill</label>
            <div className="flex flex-wrap gap-2">
              {allSkills?.map((skill) => (
                <button
                  key={skill}
                  onClick={() => setFilterSkill(skill)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    filterSkill === skill
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground hover:bg-muted/80'
                  }`}
                >
                  {skill === 'all' ? 'All Skills' : skill}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Technician Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredTechnicians?.map((tech) => {
          const assignedOrders = getTechnicianWorkOrders(tech?.id);
          
          return (
            <div
              key={tech?.id}
              className="bg-card rounded-xl border border-border p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name="User" size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{tech?.name}</h3>
                    {getAvailabilityBadge(tech?.availability)}
                  </div>
                </div>
              </div>

              {/* Skills */}
              <div className="mb-4">
                <p className="text-xs text-muted-foreground mb-2">Skills</p>
                <div className="flex flex-wrap gap-1">
                  {tech?.skills?.map((skill) => (
                    <span
                      key={skill}
                      className="px-2 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              {/* Workload */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-muted-foreground">Current Workload</p>
                  <span className={`text-sm font-semibold ${getWorkloadColor(tech?.currentWorkload)}`}>
                    {tech?.currentWorkload} tasks
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      tech?.currentWorkload >= 5 ? 'bg-error' :
                      tech?.currentWorkload >= 3 ? 'bg-warning' : 'bg-success'
                    }`}
                    style={{ width: `${Math.min((tech?.currentWorkload / 6) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>

              {/* Assigned Work Orders */}
              {assignedOrders?.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-muted-foreground mb-2">Active Work Orders</p>
                  <div className="space-y-2">
                    {assignedOrders?.slice(0, 2)?.map((wo) => (
                      <div
                        key={wo?.id}
                        className="text-xs p-2 rounded bg-muted border border-border"
                      >
                        <div className="font-medium text-foreground truncate">{wo?.title}</div>
                        <div className="text-muted-foreground mt-1">
                          Priority: <span className="capitalize">{wo?.priority}</span>
                        </div>
                      </div>
                    ))}
                    {assignedOrders?.length > 2 && (
                      <p className="text-xs text-muted-foreground text-center">
                        +{assignedOrders?.length - 2} more
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  iconName="Eye"
                  iconPosition="left"
                  onClick={() => setSelectedTechnician(tech)}
                >
                  View Details
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  fullWidth
                  iconName="UserPlus"
                  iconPosition="left"
                  disabled={tech?.availability === 'unavailable'}
                >
                  Assign Task
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredTechnicians?.length === 0 && (
        <div className="bg-card rounded-xl p-12 border border-border text-center">
          <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Technicians Found</h3>
          <p className="text-muted-foreground">No technicians match the selected skill filter</p>
        </div>
      )}
    </div>
  );
};

export default TechnicianAssignment;